<template>
    <v-row justify="center">
        <!-- Pot Size Dialog -->
        <PotSizeEditDialog
            :dialog.sync="potSizeDialog"
            v-on:item-created="potSizeAdded"
        ></PotSizeEditDialog>

        <!-- Main dialog -->
        <v-dialog v-model="dialog" scrollable persistent max-width="800px">
            <v-card>
                <v-alert v-if="error" tile type="error">{{ error }}</v-alert>
                <v-card-title class="primary white--text">{{
                    formTitle
                }}</v-card-title>
                <v-card-text class="mt-4">
                    <v-form v-model="isFormValid" ref="form">
                        <v-row align="center">
                            <!-- Plant Pot Sizes -->
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="editedItem.potSizeNodeId"
                                    :items="potSizes"
                                    item-text="node.potSizeCm"
                                    item-value="node.id"
                                    :loading="isLoadingPotSizes"
                                    :rules="[rules.listRequired]"
                                    label="Select Pot Size*"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                    >
                                        <v-chip color="primary"
                                            >{{
                                                item.node.potSizeCm
                                            }}
                                            cm</v-chip
                                        >
                                    </template>
                                    <template
                                        v-if="hasPermission('add_potsize')"
                                        v-slot:append-outer
                                    >
                                        <v-btn text small tabindex="-1">
                                            <v-icon
                                                color="grey"
                                                @click="addPotSize"
                                                tabindex="-1"
                                                >add</v-icon
                                            >
                                        </v-btn>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Name -->
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="editedItem.templateName"
                                    :counter="100"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(50)
                                    ]"
                                    label="Template Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- Failure % -->
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="editedItem.failureRate"
                                    :rules="[rules.required]"
                                    suffix="%"
                                    type="number"
                                    label="Failure %"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Items divider -->
                        <v-row align="center" class="mx-0 mb-3">
                            <v-divider></v-divider>
                            <span class="pl-3 pr-3">Cost Items</span>
                            <v-divider></v-divider>
                        </v-row>

                        <!-- items -->
                        <v-row
                            v-for="(item, index) in editedItem.items"
                            :key="index"
                            dense
                        >
                            <!-- item name -->
                            <v-col cols="7" md="6">
                                <v-text-field
                                    v-model="item.itemName"
                                    :rules="[
                                        rules.required,
                                        rules.maxLength(50)
                                    ]"
                                    label="Cost Name*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <!-- cost value -->
                            <v-col cols="4" md="5">
                                <v-text-field
                                    v-model="item.costValue"
                                    :rules="[rules.required]"
                                    prefix="€"
                                    type="number"
                                    label="Cost Value*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="1" class="text-right">
                                <v-btn
                                    icon
                                    @click="removeItemFromList(index)"
                                    tabindex="-1"
                                >
                                    <v-icon>close</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn
                                    color="primary"
                                    block
                                    outlined
                                    @click="addNewItem()"
                                >
                                    + Add
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pl-8 pr-5">
                    <strong class="mr-2"
                        >Total €{{ calculatedTotalCost.toFixed(2) }}
                    </strong>
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small color="grey" v-on="on"
                                >error_outline</v-icon
                            >
                        </template>
                        <span>Total = SUM(Cost Items) / (100% - Failure%)</span>
                    </v-tooltip>
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close" tabindex="-1"
                        >Cancel</v-btn
                    >
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        :disabled="!isFormValid"
                        color="primary"
                        @click="save"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import PotSizeEditDialog from "@/components/plants/PotSizeEditDialog.vue";

export default {
    name: "plant-fixed-cost-templates-edit-dialog",
    props: ["dialog", "object", "isCopy", "defaultPotSizeNodeId"],
    components: {
        PotSizeEditDialog
    },
    data() {
        return {
            isFormValid: false,
            error: null,
            isLoadingPotSizes: false,
            isSaving: false,
            potSizeDialog: false,
            editedItem: {
                id: "",
                templateName: "",
                description: null,
                failureRate: 0,
                totalCost: 0,
                potSizeNodeId: null,
                items: []
            },
            defaultItem: {
                id: "",
                templateName: "Default",
                description: null,
                failureRate: 0,
                totalCost: 0,
                potSizeNodeId: null,
                items: [{ itemName: null, costValue: null }]
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
                // nameRules: [
                //     v => !!v || "Name is required",
                //     v =>
                //         v.length <= 50 || "Name must be less than 50 characters"
                // ]
            }
            // departmentDialog: false,
            // plantVariantDialog: false
        };
    },
    computed: {
        potSizes: function() {
            return this.$store.state.potSizes.potSizes;
        },
        formTitle() {
            return this.isCopy
                ? "Copy Template"
                : this.object
                ? "Edit Template"
                : "New Template";
        },
        calculatedTotalCost() {
            let _totalCost = this.editedItem.items.reduce(function(
                accumulator,
                item
            ) {
                return accumulator + parseFloat(item.costValue || 0);
            },
            0);

            if (this.editedItem.failureRate != 100) {
                _totalCost =
                    (_totalCost / (100 - this.editedItem.failureRate)) * 100;
            }

            return _totalCost;
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    watch: {
        // reset form when dialog open or close
        dialog(val) {
            // if open dialog, initialize selections
            if (val) {
                this.initializeSelections();
            }

            this.resetForm();
        }
    },
    methods: {
        initializeSelections() {
            // fetch potsizes
            this.isLoadingPotSizes = true;
            this.$store
                .dispatch("potSizes/fetchPotSizes")
                .then(() => {})
                .catch(error => {
                    console.log(error);
                })
                .finally(() => (this.isLoadingPotSizes = false));
        },

        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            // when editing, set form with object
            if (this.dialog && this.object) {
                this.editedItem.id = this.object.id;
                this.editedItem.templateName = this.object.templateName;
                this.editedItem.failureRate = this.object.failureRate * 100;
                this.editedItem.potSizeNodeId = this.object.potSize.id;
                this.editedItem.items = this.object.plantFixedCostTemplateItemSet.edges.map(
                    item => {
                        return {
                            itemName: item.node.itemName,
                            costValue: item.node.costValue
                        };
                    }
                );
            }
            // when new or closing, set form with default
            else {
                this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
                this.editedItem.potSizeNodeId = this.defaultPotSizeNodeId;
            }
        },

        close() {
            this.$emit("update:dialog", false);
            this.$emit("update:object", null);
            this.$emit("update:isCopy", false);
            this.resetForm();
        },

        save() {
            // check if form valid
            if (!this.$refs.form.validate()) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // if editing
            if (this.object && !this.isCopy) {
                this.$apollo
                    .mutate({
                        // Query
                        mutation: gql`
                            mutation updatePlantFixedCostTemplate(
                                $input: UpdatePlantFixedCostTemplateInput!
                            ) {
                                updatePlantFixedCostTemplate(input: $input) {
                                    plantFixedCostTemplate {
                                        id
                                        templateName
                                        failureRate
                                        totalCost
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        plantFixedCostTemplateItemSet {
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                            totalCount
                                        }
                                    }
                                }
                            }
                        `,
                        // Parameters
                        variables: {
                            input: {
                                id: this.object.id,
                                potSizeNodeId: this.editedItem.potSizeNodeId,
                                templateName: this.editedItem.templateName,
                                description: this.editedItem.description,
                                failureRate: this.editedItem.failureRate / 100,
                                items: this.editedItem.items
                            }
                        }
                    })
                    .then(() => {
                        this.$emit("item-created", false);
                        this.close();

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Saved!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        // Error
                        console.error(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
            // if new
            else {
                this.$apollo
                    .mutate({
                        // Query
                        mutation: gql`
                            mutation createPlantFixedCostTemplate(
                                $input: CreatePlantFixedCostTemplateInput!
                            ) {
                                createPlantFixedCostTemplate(input: $input) {
                                    plantFixedCostTemplate {
                                        id
                                        templateName
                                        failureRate
                                        totalCost
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        plantFixedCostTemplateItemSet {
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                            totalCount
                                        }
                                    }
                                }
                            }
                        `,
                        // Parameters
                        variables: {
                            input: {
                                potSizeNodeId: this.editedItem.potSizeNodeId,
                                templateName: this.editedItem.templateName,
                                description: this.editedItem.description,
                                failureRate: this.editedItem.failureRate / 100,
                                items: this.editedItem.items
                            }
                        }
                    })
                    .then(response => {
                        this.$emit(
                            "item-created",
                            response.data.createPlantFixedCostTemplate
                        );
                        this.close();

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Saved!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        // Error
                        console.error(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
        },

        addPotSize() {
            this.potSizeDialog = true;
        },

        potSizeAdded(item) {
            this.editedItem.potSizeNodeId = item.id;
        },

        addNewItem() {
            this.editedItem.items.push({ itemName: "", costValue: "" });
        },

        removeItemFromList(index) {
            if (index > -1) {
                this.editedItem.items.splice(index, 1);
            }
        }
    }
};
</script>